import { Link } from "react-router-dom";
import { ArrowRight, Box, Calculator, MapPin, ShieldCheck, Truck, Zap, Clock, MessageCircle } from "lucide-react";
import Logo from "@/components/Logo";
import PublicRateCalculator from "@/components/PublicRateCalculator";
import PublicDeliveryPackCard from "@/components/PublicDeliveryPackCard";
import { SERVICE_OPERATOR, informationUrl, PRIVATE_DRIVER_INFO_TEXT } from "@/lib/contact";

// DapNow visuals — territorio/mobilità coerenti, neutri rispetto a brand terzi
// Hero: mano sul volante / commuter feel — confermata dall'utente come "molto bella"
const HERO_IMG = "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?auto=format&fit=crop&w=1920&q=80";
// Phase 43.38 — Per la sezione driver riuso la stessa foto dell'hero (volante).
// Gli altri ID Unsplash si sono rivelati instabili (Mario Bros figurines, Mustang,
// scrivania rose gold...). Riusare il volante garantisce coerenza visiva certa.
const DRIVER_IMG = HERO_IMG;

export default function Landing() {
  return (
    <div className="min-h-screen bg-white">
      {/* Top bar — Phase 43.39: composizione premium, logo più grande, switch segmentato Privati/Aziende */}
      <header className="absolute top-0 inset-x-0 z-30 pt-[env(safe-area-inset-top)]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 pt-3 pb-2 md:py-0 md:min-h-28 flex flex-col md:flex-row items-center justify-between gap-2 md:gap-4">
          <Logo variant="light" size="lg" />
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Switch segmentato Privati/Aziende — desktop */}
            <div className="hidden md:inline-flex items-center rounded-full border border-white/15 bg-white/5 backdrop-blur p-1">
              <span className="px-4 h-9 inline-flex items-center rounded-full text-[13px] font-semibold bg-white text-dapnow-ink" aria-current="page">
                Per privati
              </span>
              <Link
                to="/business"
                data-testid="landing-business-link"
                className="px-4 h-9 inline-flex items-center rounded-full text-[13px] font-semibold text-white/75 hover:text-white transition"
              >
                Per aziende
              </Link>
            </div>
            <Link
              to="/login?audience=private"
              data-testid="landing-login-link"
              className="inline-flex h-10 sm:h-11 px-3 sm:px-4 items-center rounded-xl text-[13px] sm:text-sm font-semibold text-white/90 hover:text-white"
            >
              Accedi
            </Link>
            <Link
              to="/register?audience=private"
              data-testid="landing-register-btn"
              className="inline-flex h-10 sm:h-11 px-3.5 sm:px-5 items-center rounded-xl bg-white text-dapnow-ink text-[13px] sm:text-sm font-semibold hover:bg-white/90 transition shadow-sm"
            >
              <span className="sm:hidden">Inizia</span>
              <span className="hidden sm:inline">Inizia ora</span>
              <ArrowRight className="ml-1.5 h-3.5 w-3.5 sm:h-4 sm:w-4" />
            </Link>
          </div>
        </div>
        {/* Switch segmentato — mobile, centrato sotto l'header */}
        <div className="md:hidden flex justify-center px-4 pt-1">
          <div className="inline-flex items-center rounded-full border border-white/15 bg-white/5 backdrop-blur p-0.5" data-testid="landing-segment-mobile">
            <span className="px-3.5 h-7 inline-flex items-center rounded-full text-[11px] font-semibold bg-white text-dapnow-ink" aria-current="page">
              Per privati
            </span>
            <Link
              to="/business"
              data-testid="landing-business-link-mobile"
              className="px-3.5 h-7 inline-flex items-center rounded-full text-[11px] font-semibold text-white/75 hover:text-white"
            >
              Per aziende
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-dapnow-ink text-white">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="h-full w-full object-cover opacity-55" />
          <div className="absolute inset-0 bg-gradient-to-tr from-black/85 via-black/55 to-transparent" />
        </div>
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 pt-56 sm:pt-60 md:pt-48 pb-20 sm:pb-32">
          <p className="overline text-white/70 tracking-widest" data-testid="hero-tagline">DapNow · Ticino e Grigioni italiano</p>
          <h1 className="mt-3 max-w-3xl font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.05]">
            Consegne in giornata.<br />
            <span className="text-dapnow-blue">Vicino a te.</span>
          </h1>
          <p className="mt-5 max-w-xl text-base sm:text-lg text-white/80 leading-relaxed">
            Consegne per privati con driver interni selezionati e approvati, in Ticino e nei Grigioni italiano.
            Ritiro Standard dalle 06:30 alle 18:00, consegna entro le 21:00. Ritiri serali con tempi da concordare.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#calcola-prezzo" data-testid="hero-cta-price" className="btn-primary">
              <Calculator className="mr-2 h-4 w-4" /> Calcola il prezzo
            </a>
            <Link to="/register?audience=private" data-testid="hero-cta-customer" className="btn-ghost bg-white/10 border-white/20 text-white hover:bg-white/20">
              Organizza una consegna <ArrowRight className="ml-1.5 h-4 w-4" />
            </Link>
            <Link to="/become-driver" data-testid="hero-cta-driver" className="btn-ghost bg-white/10 border-white/20 text-white hover:bg-white/20">
              Driver privati · In arrivo
            </Link>
          </div>

          <dl className="mt-12 grid grid-cols-3 max-w-md gap-6">
            {[
              { k: "In giornata", v: "Con il team DapNow" },
              { k: "Locale", v: "Ticino e Grigioni italiano" },
              { k: "CHF", v: "Prezzi chiari" },
            ].map((s) => (
              <div key={s.v}>
                <dt className="font-display text-2xl font-bold tracking-tight text-white">{s.k}</dt>
                <dd className="text-xs text-white/85 font-medium tracking-tight">{s.v}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <div id="calcola-prezzo" className="scroll-mt-6">
        <PublicRateCalculator audience="private" />
      </div>

      {/* Come funziona il modello DapNow */}
      <section className="bg-white border-b border-dapnow-line">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-20">
          <p className="overline text-dapnow-blue" data-testid="commuter-section-tag">Il servizio DapNow</p>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold tracking-tight max-w-3xl">
            Dal ritiro alla consegna, <span className="text-dapnow-blue">nella stessa giornata</span>.
          </h2>
          <p className="mt-3 text-dapnow-muted max-w-2xl">
            Affida documenti e pacchi al nostro team per le tue consegne locali.
            Inserisci ritiro e destinazione, verifica prezzo e disponibilità e segui lo stato della spedizione.
            La possibilità di consegnare in giornata dipende da tratta, orario e disponibilità del servizio.
          </p>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="commuter-tiers">
            <CommuterCard
              icon={Clock}
              title="Consegne in giornata"
              tagline="Tutti i giorni · 06:30–21:00"
              desc="Standard: consegna entro le 21:00 per ritiri entro le 18:00, secondo disponibilità della flotta. Per ritiri serali, concorda prima i tempi."
              tid="same-day-service"
            />
            <CommuterCard
              icon={Truck}
              title="Il nostro team"
              tagline="Driver interni selezionati"
              desc="Le consegne sono affidate a driver approvati da DapNow. Puoi consultare gli aggiornamenti della spedizione dal tuo account."
              tid="internal-fleet-service"
            />
            <CommuterCard
              icon={MapPin}
              title="Solo locale"
              tagline="Ticino e Grigioni italiano"
              desc="Restiamo focalizzati sull'area Ticino e Grigioni italiano. Niente tratte oltreconfine: la rete locale lavora meglio."
              tid="commuter-tier-area"
            />
          </div>
        </div>
      </section>

      {/* Account types */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-20" data-testid="private-account-options">
        <p className="overline">Per chi è DapNow</p>
        <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold tracking-tight max-w-2xl">
          Un account per le tue consegne personali.
        </h2>

        <div className="mt-10 grid sm:grid-cols-2 gap-4">
          {[
            { i: Box, t: "Privato", d: "Organizza consegne in giornata per parenti, amici e destinatari locali." },
            { i: Truck, t: "Driver privato · In arrivo", d: "Vuoi consegnare lungo i tuoi tragitti? Il servizio sarà disponibile prossimamente.", link: "/become-driver" },
          ].map((c) => (
            <div key={c.t} className="card-soft p-6">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-dapnow-blue-soft text-dapnow-blue">
                <c.i className="h-5 w-5" />
              </span>
              <h3 className="mt-4 font-display text-xl font-semibold tracking-tight">{c.t}</h3>
              <p className="mt-1 text-sm text-dapnow-muted">{c.d}</p>
              {c.link && <Link to={c.link} className="inline-flex mt-4 text-sm font-semibold text-dapnow-blue hover:underline">Scopri il progetto →</Link>}
            </div>
          ))}
        </div>
      </section>

      {/* Process */}
      <PublicDeliveryPackCard />

      <section className="bg-dapnow-bg py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="overline">Come funziona</p>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold tracking-tight">
              Tre passi. Un pacco. <span className="text-dapnow-red">Tutto sotto controllo.</span>
            </h2>
            <ol className="mt-8 space-y-5">
              {[
                { i: MapPin, t: "Inserisci ritiro e consegna", d: "Indirizzi precisi, contatti e note utili." },
                { i: Box, t: "Descrivi il pacco", d: "Dimensioni, contenuto e orario di ritiro desiderato." },
                { i: Zap, t: "Verifica e conferma", d: "Controlla disponibilità e prezzo, poi segui lo stato della consegna." },
              ].map((s, i) => (
                <li key={s.t} className="flex gap-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white border border-dapnow-line text-dapnow-blue font-display font-bold">
                    {i + 1}
                  </span>
                  <div>
                    <h4 className="font-display text-lg font-semibold tracking-tight">{s.t}</h4>
                    <p className="text-sm text-dapnow-muted">{s.d}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
          <div className="relative">
            {/* Phase 43.38 — Blocco visuale controllato (no immagine remota imprevedibile).
                Sfondo gradient + icona Box + tracciato stilizzato. Coerente con DapNow. */}
            <div
              className="rounded-3xl border border-dapnow-line shadow-[0_30px_80px_rgba(0,0,0,0.08)] aspect-[4/5] overflow-hidden relative bg-gradient-to-br from-dapnow-blue-soft via-white to-emerald-50"
              data-testid="process-visual"
            >
              {/* Linee tratteggiate stilizzate del tragitto */}
              <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 500" preserveAspectRatio="none" aria-hidden="true">
                <defs>
                  <linearGradient id="rt" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#0052FF" stopOpacity="0.5" />
                    <stop offset="100%" stopColor="#0052FF" stopOpacity="0.15" />
                  </linearGradient>
                </defs>
                <path d="M 40 460 Q 130 380 180 320 T 280 180 T 360 60"
                  fill="none" stroke="url(#rt)" strokeWidth="4" strokeDasharray="9 7" strokeLinecap="round" />
                <circle cx="40" cy="460" r="10" fill="#0052FF" />
                <circle cx="360" cy="60" r="10" fill="#10B981" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="rounded-3xl bg-white shadow-xl ring-1 ring-dapnow-line p-6 -translate-y-4">
                  <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-dapnow-blue text-white">
                    <Box className="h-7 w-7" />
                  </span>
                  <p className="overline mt-3 text-dapnow-blue">Il tuo pacco</p>
                  <p className="font-display text-lg font-bold tracking-tight">In viaggio</p>
                  <p className="text-xs text-dapnow-muted mt-0.5">Lugano → Bellinzona</p>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 card-soft p-4 w-56 hidden sm:block animate-float">
              <p className="overline text-[10px]">Consegne locali</p>
              <p className="font-display text-2xl font-bold tracking-tight mt-0.5">In giornata</p>
              <p className="text-xs text-dapnow-muted mt-1">Prezzo e disponibilità prima della conferma</p>
            </div>
          </div>
        </div>
      </section>

      {/* Driver CTA */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-20 grid lg:grid-cols-2 gap-12 items-center">
        <div className="order-2 lg:order-1">
          <img src={DRIVER_IMG} alt="Trasportatore DapNow" className="rounded-3xl border border-dapnow-line shadow-[0_30px_80px_rgba(0,0,0,0.08)] aspect-[4/3] object-cover" />
        </div>
        <div className="order-1 lg:order-2">
          <p className="overline text-dapnow-blue">Driver privati · In arrivo</p>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold tracking-tight">
            I tuoi tragitti, <span className="text-dapnow-blue">una nuova possibilità.</span>
          </h2>
          <p className="mt-3 text-dapnow-muted max-w-lg">
            Stiamo preparando il servizio per chi vuole consegnare pacchi lungo i propri tragitti.
            Se ti interessa diventare driver privato DapNow, puoi già richiedere informazioni.
          </p>
          <ul className="mt-6 space-y-3 text-sm">
            {[
              "Un progetto dedicato ai tragitti locali",
              "Ticino e Grigioni italiano",
              "Modalità di partecipazione disponibili con l’attivazione del servizio",
            ].map((b) => (
              <li key={b} className="flex items-start gap-2">
                <ShieldCheck className="h-5 w-5 text-dapnow-blue mt-0.5" /> <span>{b}</span>
              </li>
            ))}
          </ul>
          <div className="mt-7">
            <a href={informationUrl(PRIVATE_DRIVER_INFO_TEXT)} target="_blank" rel="noopener noreferrer" data-testid="driver-cta-section" className="btn-primary">
              <MessageCircle className="mr-2 h-4 w-4" /> Richiedi informazioni
            </a>
            <Link to="/become-driver" className="block mt-4 text-sm font-semibold text-dapnow-blue hover:underline">Scopri il progetto driver privati →</Link>
            <p className="text-xs text-dapnow-muted mt-3 max-w-md">
              Il pulsante prepara un’email a DapNow. Il servizio per driver privati non è ancora attivo;
              oggi le consegne sono gestite dal team interno DapNow.
            </p>
          </div>
        </div>
      </section>

      <footer className="border-t border-dapnow-line">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8 flex flex-col sm:flex-row flex-wrap gap-6 items-center justify-between">
          <Logo size="sm" />
          <div className="flex flex-wrap gap-5 text-xs text-dapnow-muted">
            <Link to="/legal" className="hover:underline">Gestore e contatti</Link>
            <Link to="/business" className="hover:text-dapnow-ink">Per aziende</Link>
            <Link to="/login?audience=private" className="hover:text-dapnow-ink">Accedi</Link>
            <Link to="/become-driver" className="hover:text-dapnow-ink">Driver privati · In arrivo</Link>
          </div>
          <p className="text-xs text-dapnow-muted">© {new Date().getFullYear()} {SERVICE_OPERATOR}.</p>
        </div>
      </footer>
    </div>
  );
}

function CommuterCard({ icon: Icon, title, tagline, desc, accent, tid }) {
  const iconBg =
    accent === "green" ? "bg-emerald-50 text-emerald-700"
    : accent === "red" ? "bg-red-50 text-dapnow-red"
    : "bg-dapnow-blue-soft text-dapnow-blue";
  return (
    <div className="card-soft p-6" data-testid={tid}>
      <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${iconBg}`}>
        <Icon className="h-5 w-5" />
      </span>
      <h3 className="mt-4 font-display text-xl font-semibold tracking-tight">{title}</h3>
      <p className="mt-1 text-xs font-semibold text-dapnow-blue uppercase tracking-wider">{tagline}</p>
      <p className="mt-2 text-sm text-dapnow-muted leading-relaxed">{desc}</p>
    </div>
  );
}
